import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../buttons/IconButton';
import GeneralLoader from '../loaders/GeneralLoader';
import { getContactIndividualId } from '../../utils/idUtils';
import { openInNewTabButtonProps } from '../../utils/openInNewTab';
import { formatOwnershipShare } from '../../utils/ownership';
import { PUNCTUATION } from '../../utils/constants/notation';
import { FIELD_SOURCE, dataSourceClass } from '../../utils/constants/dataSources';
import useEnrichmentAccess from '../../hooks/useEnrichmentAccess';
import useContactReveal from '../../hooks/useContactReveal';

const STATUS_FOUND = 'found';
const STATUS_NOT_FOUND = 'not_found';
const STATUS_LOADING = 'loading';

// TD-2273: the discovered field layout follows the ROW's origin, not just the
// render context — a persisted discovered row (person_crefo_id 'disc_<uuid>',
// BE-minted at discover-reveal, or an explicit source) must keep the discovered
// field set even when it arrives via the stored keyContacts feed.
const rowIsDiscovered = (source, contact) =>
  source === 'discovered' ||
  contact?.source === 'discovered' ||
  String(contact?.person_crefo_id ?? '').startsWith('disc_');

// Discovered contacts carry a BE-built `address` string ("City, Country",
// TD-2272); stored CREFO contacts compose it from street/city/country.
const composeContactAddress = (contact, isDiscoveredRow) => {
  if (isDiscoveredRow) return contact.address;
  const cityLine =
    contact.city && contact.postcode
      ? `${contact.postcode} ${contact.city.trim()}`
      : contact.city || contact.postcode;
  return [contact.street, cityLine, contact.country].filter(Boolean).join(', ');
};

// When the field is NOT being fetched, display precedence is: cached BE value >
// locally revealed value > "not found" > the reveal button. A field IS being
// fetched — and shows the spinner OVER any of those — when a single reveal is in
// flight (hook state 'loading') OR it's part of an in-flight bulk job that will
// actually query it (TD-2427): a forced re-fetch (`bulkForce` = skip OFF, which
// re-queries already-enriched fields and so must animate over the exposed
// value/"Not found") or a field that isn't resolved yet. `bulkActive` = this
// field was submitted for this card.
const deriveRevealDisplay = (
  cachedStatus,
  cachedValue,
  state,
  { bulkActive = false, bulkForce = false } = {}
) => {
  const resolved =
    cachedStatus === STATUS_FOUND ||
    cachedStatus === STATUS_NOT_FOUND ||
    state.status === STATUS_FOUND ||
    state.status === STATUS_NOT_FOUND;
  const fetching = state.status === STATUS_LOADING || (bulkActive && (bulkForce || !resolved));
  const inline =
    !fetching &&
    ((cachedStatus === STATUS_FOUND ? cachedValue : null) ||
      (state.status === STATUS_FOUND ? state.value : null));
  const showNotFound =
    !fetching &&
    !inline &&
    (cachedStatus === STATUS_NOT_FOUND || state.status === STATUS_NOT_FOUND);
  return { inline, showNotFound, loading: fetching };
};

// One reveal row (email or phone). Precedence handled by early returns rather
// than a nested ternary for readability.
const ContactRevealField = ({
  icon,
  display,
  onClick,
  buttonLabel,
  tooltip,
  fieldKey,
  buttonsDisabled,
  disabledTooltip
}) => {
  // Icon shown to the LEFT of a revealed value / "Not found" / loader. In the
  // button state the icon instead moves INSIDE the button (TD-2420) so the
  // control reads as one unit.
  const leadingIcon = <img className="contact-card-reveal-icon" src={icon} alt="" />;
  const renderContent = () => {
    if (display.inline) {
      return (
        <>
          {leadingIcon}
          <div className="contact-card-reveal-value">{display.inline}</div>
        </>
      );
    }
    if (display.showNotFound) {
      return (
        <>
          {leadingIcon}
          <div className="contact-card-reveal-not-found">Not found</div>
        </>
      );
    }
    // Idle AND loading both render the button, so the reveal control stays in
    // place and the spinner shows INSIDE it (TD-2496) rather than swapping the
    // button out for a detached loader. While loading the button is click-
    // guarded and aria-busy.
    return (
      <button
        type="button"
        className="contact-card-reveal-button"
        // Use aria-disabled + click guard (not native `disabled`) so the button
        // stays focusable, the sr-only "insufficient credits" copy remains
        // reachable, and the native `title` tooltip still fires. WCAG ARIA14.
        onClick={buttonsDisabled || display.loading ? undefined : onClick}
        aria-disabled={buttonsDisabled || display.loading || undefined}
        aria-busy={display.loading || undefined}
        title={buttonsDisabled ? disabledTooltip : tooltip}
      >
        {display.loading ? (
          <>
            <GeneralLoader wrapClassName="contact-card-reveal-btn-loader" />
            {/* <output> carries an implicit status live-region role, so it
                announces the in-flight reveal without an explicit role. */}
            <output className="contact-card-reveal-sr-only">Revealing {fieldKey}</output>
          </>
        ) : (
          <>
            {/* TD-2420: icon lives inside the button so it reads as one control. */}
            <img className="contact-card-reveal-btn-icon" src={icon} alt="" />
            <span>{buttonLabel}</span>
            {buttonsDisabled && (
              <span className="contact-card-reveal-sr-only">: {disabledTooltip}</span>
            )}
          </>
        )}
      </button>
    );
  };

  return (
    <div className={`contact-card-reveal-field contact-card-reveal-${fieldKey}`}>
      {renderContent()}
    </div>
  );
};

ContactRevealField.propTypes = {
  icon: PropTypes.string.isRequired,
  display: PropTypes.shape({
    inline: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showNotFound: PropTypes.bool,
    loading: PropTypes.bool
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  fieldKey: PropTypes.string.isRequired,
  buttonsDisabled: PropTypes.bool,
  disabledTooltip: PropTypes.string
};

// Name + standalone job title (TD-2273/TD-2358) + the action
// icons (view-details, LinkedIn, bulk-select checkbox).
const ContactNameRow = ({
  contact,
  onViewDetails,
  onOpenDetailsInNewTab,
  onLinkedInClick,
  canViewDetails,
  showSelectionCheckbox,
  selected,
  onSelectionToggle
}) => (
  <div className="name-row">
    <div className="name">
      {contact.first_name} {contact.last_name}
      {/* Standalone Surfe job title (TD-2358) — shown for BOTH discovered and
          stored contacts whenever the BE provides it (stored contacts get it
          once the tenant holds an enrichment row with surfe_job_title).
          Distinct from Role = seniority. */}
      {contact.job_title ? <div className="contact-card-job-title">{contact.job_title}</div> : null}
    </div>
    <div className="name-row-actions">
      {onViewDetails && (
        <IconButton
          wrapClassName="view-details-contact-button"
          icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
          type={ICON_BUTTON_TYPES.BLUE}
          {...openInNewTabButtonProps({
            activate: () => onViewDetails(contact),
            openInNewTab: onOpenDetailsInNewTab && (() => onOpenDetailsInNewTab(contact)),
            enabled: canViewDetails
          })}
          tooltipText={canViewDetails ? 'View individual profile' : 'Details not available'}
          disabled={!canViewDetails}
        />
      )}
      <IconButton
        wrapClassName="linkedin-contact-button"
        type={ICON_BUTTON_TYPES.BLUE}
        icon={ICON_BUTTON_ICONS.LINKEDIN}
        onClick={onLinkedInClick}
        tooltipText="Find on LinkedIn"
      />
      {showSelectionCheckbox && (
        <label
          className="contact-card-select-checkbox"
          aria-label={`Select ${contact.first_name} ${contact.last_name}`}
        >
          <input type="checkbox" checked={selected} onChange={onSelectionToggle} />
        </label>
      )}
    </div>
  </div>
);

ContactNameRow.propTypes = {
  contact: PropTypes.object.isRequired,
  onViewDetails: PropTypes.func,
  onOpenDetailsInNewTab: PropTypes.func,
  onLinkedInClick: PropTypes.func.isRequired,
  canViewDetails: PropTypes.bool,
  showSelectionCheckbox: PropTypes.bool,
  selected: PropTypes.bool,
  onSelectionToggle: PropTypes.func.isRequired
};

// A shareholder contact's ownership share rides in the existing "Is also a shareholder" cell.
// `formatOwnershipShare` returns null when there is nothing to show, so a shareholder we hold no
// percentage for falls through to the plain "Yes" this card has always rendered. That is the
// common case, since the BE only populates `beneficial_ownership` for owners flagged ultimate,
// and it is why this needs no placeholder of its own.
const shareholderValue = (contact) => {
  const share = formatOwnershipShare(contact.beneficial_ownership);
  return share ? `Yes (${share})` : 'Yes';
};

// TD-2124: a look-through (HoldCo) shareholder gets "indirect" in plain text after the green
// pill. An indirect owner who holds no role at the target also names the company they hold
// through (`anchor_company_name`, the same company the reveal goes to), e.g.
// "indirect, via Doll Co-Investment GmbH & Co. KG". Indirect managers carry no anchor and keep
// the bare "indirect". Returns null for a direct shareholder.
const indirectShareholderQualifier = (contact) => {
  if (contact.shareholder_type !== 'indirect') return null;
  const anchorName =
    typeof contact.anchor_company_name === 'string' ? contact.anchor_company_name.trim() : '';
  return anchorName ? `indirect, via ${anchorName}` : 'indirect';
};

// Role / Age / Country / Shareholder / Address rows. Discovered rows show 'n/a'
// for CREFO-only fields (Age, shareholder) and the Surfe country when present
// (TD-2273). A missing Age/Country renders the shared grey "n/a" pill rather
// than bold value text, so every "n/a" on the card reads the same as the
// shareholder placeholder (TD-2674).
const ContactDetailRows = ({ contact, isDiscoveredRow, address }) => {
  // Discovered rows never carry CREFO age; some stored contacts also lack it
  // (null/undefined/''). Country is usually present on CREFO persons, but
  // persons.country is nullable (~0.1–0.5% missing land-iso-code), so treat a
  // falsy country the same on stored and discovered rows. Numeric 0 remains a
  // real Age value.
  const ageIsEmpty = isDiscoveredRow || contact.age == null || contact.age === '';
  const countryIsEmpty = !contact.country;
  const shareholderQualifier =
    !isDiscoveredRow && contact.is_shareholder ? indirectShareholderQualifier(contact) : null;
  return (
    <>
      <div className="contact-card-row">
        <div className="left">
          <div className="label">Role</div>
          <div className="value">{contact.manager_function}</div>
        </div>
        <div className="right">
          <div className="label">Age</div>
          <div className={`value${ageIsEmpty ? ' empty-value' : ''}`}>
            {ageIsEmpty ? PUNCTUATION.EMPTY_VALUE : contact.age}
          </div>
        </div>
      </div>
      <div className="contact-card-row">
        <div className="left">
          <div className="label">Country of residency</div>
          <div className={`value${countryIsEmpty ? ' empty-value' : ''}`}>
            {countryIsEmpty ? PUNCTUATION.EMPTY_VALUE : contact.country}
          </div>
        </div>
        <div className="right">
          <div className="label">Is also a shareholder</div>
          {isDiscoveredRow ? (
            // TD-2420: render the discovered placeholder with the same grey
            // "n/a" pill the stored card uses for a non-shareholder, so the
            // styling is consistent across contact types.
            <div className="value is-shareholder no">{PUNCTUATION.EMPTY_VALUE}</div>
          ) : (
            <div
              className={`value is-shareholder ${contact.is_shareholder ? 'yes' : 'no'}${
                shareholderQualifier ? ' has-qualifier' : ''
              }`}
            >
              {contact.is_shareholder ? shareholderValue(contact) : PUNCTUATION.EMPTY_VALUE}
            </div>
          )}
          {!!shareholderQualifier && (
            <div className="value shareholder-qualifier">{shareholderQualifier}</div>
          )}
        </div>
      </div>
      {!!address && (
        <div className="contact-card-row">
          <div className="center">
            <div className="label">Address</div>
            <div className="value">{address}</div>
          </div>
        </div>
      )}
    </>
  );
};

ContactDetailRows.propTypes = {
  contact: PropTypes.object.isRequired,
  isDiscoveredRow: PropTypes.bool,
  address: PropTypes.string
};

// "Personal contact" reveal section — the two reveal rows behind the enrichment
// gate.
const ContactRevealSection = ({
  emailDisplay,
  phoneDisplay,
  onRevealEmail,
  onRevealPhone,
  buttonsDisabled,
  disabledTooltip
}) => (
  <div className="contact-card-reveal-section">
    <div className={`contact-card-reveal-title ${dataSourceClass(FIELD_SOURCE.personalEmail)}`}>
      Personal contact
    </div>
    {/* TD-2420: email + mobile sit side by side (equal width) and wrap the
        second field to its own line when the card is too narrow. */}
    <div className="contact-card-reveal-fields">
      <ContactRevealField
        icon={ICON_BUTTON_ICONS.ENVELOPE}
        display={emailDisplay}
        onClick={onRevealEmail}
        buttonLabel="Find email"
        tooltip="1 credit"
        fieldKey="email"
        buttonsDisabled={buttonsDisabled}
        disabledTooltip={disabledTooltip}
      />
      <ContactRevealField
        icon={ICON_BUTTON_ICONS.PHONE}
        display={phoneDisplay}
        onClick={onRevealPhone}
        buttonLabel="Find mobile"
        tooltip="10 credits"
        fieldKey="phone"
        buttonsDisabled={buttonsDisabled}
        disabledTooltip={disabledTooltip}
      />
    </div>
  </div>
);

ContactRevealSection.propTypes = {
  emailDisplay: PropTypes.object.isRequired,
  phoneDisplay: PropTypes.object.isRequired,
  onRevealEmail: PropTypes.func.isRequired,
  onRevealPhone: PropTypes.func.isRequired,
  buttonsDisabled: PropTypes.bool,
  disabledTooltip: PropTypes.string
};

// observer required: the fallback useEnrichmentAccess() reads
// profilerAccessStore.isCognitoContactEnrichment (MobX computed) when no
// `access` prop is passed. Remove once ProfilerAccessStore migrates to hooks.
const ContactCard = observer(
  ({
    contact,
    onLinkedInClick,
    onViewDetails,
    onOpenDetailsInNewTab,
    selected = false,
    onSelectionChange,
    companyId,
    personCrefoId,
    access: accessProp,
    source = 'platform',
    onRevealSuccess,
    bulkRevealing
  }) => {
    // TD-2263 (Items 2+8): when rendered in a list (KeyContactsTab) the parent
    // owns a single useEnrichmentAccess() instance and passes the resulting
    // `access` object down, so mounting N cards no longer fires N
    // /subscription-status calls AND `access.enabled` is stable across cards.
    // Fall back to the hook only when no prop is supplied, so a standalone card
    // (Contact Detail page) still works. The same flag suppresses the second
    // useEnrichmentAccess inside useContactReveal to avoid a duplicate fetch +
    // re-registering the path-global cancelRequest cleanup (TD-2263).
    const enrichmentEnabled = accessProp === undefined;
    const ownAccess = useEnrichmentAccess({ enabled: enrichmentEnabled });
    const access = accessProp ?? ownAccess;

    // Stream B (TD-2085): a discovered contact has no contact_enrichment_id until
    // its first reveal. Route that first reveal to /enrichment/discover-reveal
    // (the hook's `discovered` path) with the company id as profileId + the Surfe
    // identity to persist. Once persisted (or for a stored contact) the hook uses
    // the regular /enrichment/contact/{id}/reveal endpoint. `isDiscovered` (the
    // prop) drives reveal routing / bulk selection; `isDiscoveredRow`
    // (below) drives the FIELD layout and also catches persisted discovered rows.
    const isDiscovered = source === 'discovered';
    const isDiscoveredRow = rowIsDiscovered(source, contact);
    const identity = isDiscovered
      ? {
          first_name: contact?.first_name,
          last_name: contact?.last_name,
          linkedin_url: contact?.linkedin_url,
          // TD-2667: carry the discovery-time attributes into the mint so the
          // persisted contact keeps its Role/residency after enrichment.
          // Deliberately `seniority`, NOT `manager_function`: the latter is the BE
          // `role` scalar, which falls back to the free-text job title when Surfe
          // returns no seniority (contactNormaliser `firstOf(seniorities) ??
          // jobTitle`). `seniority` is the clean enum with no fallback (TD-2292).
          // Persisting the fallback would write a job title into `seniorities[]`,
          // which the webhook's write-once top-up could never correct.
          seniority: contact?.seniority,
          job_title: contact?.job_title,
          country: contact?.country
        }
      : undefined;
    // TD-2536: reveal id for the regular /enrichment/contact/{id}/reveal path.
    // Stored CREFO rows carry a platform `id` (used as before). A PERSISTED
    // discovered row (revealed once via discover-reveal, then re-served through
    // the stored keyContacts feed) has NO platform `id` — it is keyed by its
    // `disc_<uuid>` person_crefo_id — but it DOES carry the contact_enrichment_id
    // minted at that first reveal. It also arrives WITHOUT source="discovered"
    // (the stored render passes no `source`, so `discovered` is false and the
    // discover-reveal path is off). Passing only `contact.id` left the reveal id
    // null, so issueReveal() silently returned and the next "Find email/mobile"
    // did nothing. Fall back to contact_enrichment_id so those subsequent reveals
    // hit the documented post-persist endpoint. (Bulk reveal already keyed off
    // contact_enrichment_id via partitionRevealTargets, which is why only the
    // single per-card reveal was affected.)
    const revealId = contact?.id ?? contact?.contact_enrichment_id;
    const { emailState, phoneState, revealEmail, revealPhone, insufficientCredits } =
      useContactReveal(revealId, {
        companyId,
        personCrefoId,
        enabled: enrichmentEnabled,
        discovered: isDiscovered,
        profileId: companyId,
        identity,
        // TD-2455: bubble a successful reveal up so a list parent (KeyContactsTab)
        // can refresh the credit counter it owns — same field on both stored and
        // discovered cards, so one prop covers both flows.
        onRevealSuccess
      });

    // A discovered contact can only be revealed with a first + last name —
    // /enrichment/discover-reveal (and bulk-reveal's discovered path, TD-2357)
    // 400 without it. Disable + explain, and keep it out of bulk selection.
    const missingIdentity = isDiscovered && !(contact?.first_name && contact?.last_name);

    // Selection checkbox shows only when the enrichment gate is open AND a parent
    // owns the selection state. TD-2421: discovered (ephemeral) cards now join
    // the same bulk selection — the BE mints them at reveal from their identity
    // (TD-2357) — so they are selectable whenever that identity is present.
    const showSelectionCheckbox =
      access.enabled && typeof onSelectionChange === 'function' && !missingIdentity;
    // Key spans stored (platform `id`) + discovered (`disc_` person_crefo_id)
    // rows so one selection Set in KeyContactsTab covers both (keyContactsUtils
    // `contactKey`).
    const handleSelectionToggle = (e) =>
      onSelectionChange(contact.id ?? contact.person_crefo_id, e.target.checked);
    const buttonsDisabled = insufficientCredits || access.limitReached || missingIdentity;
    const disabledTooltip = missingIdentity
      ? 'Insufficient contact data for enrichment'
      : 'Insufficient credits';

    const address = composeContactAddress(contact, isDiscoveredRow);

    // TD-2427: a bulk reveal job in flight (owned by KeyContactsTab) drives the
    // same per-field spinner as a single reveal. The parent passes the set of
    // submitted contact keys + field(s) + `force` (skip OFF = re-fetch already-
    // enriched fields). A card matches by the same key it uses for selection
    // (contact.id ?? person_crefo_id — see handleSelectionToggle /
    // keyContactsUtils `contactKey`). Only the chosen field(s) spin; on a forced
    // re-fetch the spinner replaces the exposed value/"Not found", while a skip-ON
    // job leaves an already-resolved field showing its value (not re-queried).
    const bulkKey = contact.id ?? contact.person_crefo_id;
    const inBulkReveal = !!bulkRevealing && bulkRevealing.keys.has(bulkKey);
    const bulkForce = inBulkReveal && bulkRevealing.force === true;
    const bulkEmailActive = inBulkReveal && bulkRevealing.fields.includes('email');
    const bulkPhoneActive = inBulkReveal && bulkRevealing.fields.includes('phone');

    const emailDisplay = deriveRevealDisplay(
      contact.enrichment_status_email,
      contact.personal_email,
      emailState,
      { bulkActive: bulkEmailActive, bulkForce }
    );
    const phoneDisplay = deriveRevealDisplay(
      contact.enrichment_status_phone,
      contact.mobile_phone,
      phoneState,
      { bulkActive: bulkPhoneActive, bulkForce }
    );

    return (
      <div className="contact-card">
        <div className="contact-card-inner-wrap">
          <ContactNameRow
            contact={contact}
            onViewDetails={onViewDetails}
            onOpenDetailsInNewTab={onOpenDetailsInNewTab}
            onLinkedInClick={onLinkedInClick}
            canViewDetails={getContactIndividualId(contact) !== null}
            showSelectionCheckbox={showSelectionCheckbox}
            selected={selected}
            onSelectionToggle={handleSelectionToggle}
          />
          <ContactDetailRows
            contact={contact}
            isDiscoveredRow={isDiscoveredRow}
            address={address}
          />
          {/* Reveal section visibility: Cognito gate closed OR per-company
              enrichment off → no email/phone rows (non-enrichment users see the
              card layout unchanged). */}
          {access.enabled && (
            <ContactRevealSection
              emailDisplay={emailDisplay}
              phoneDisplay={phoneDisplay}
              onRevealEmail={revealEmail}
              onRevealPhone={revealPhone}
              buttonsDisabled={buttonsDisabled}
              disabledTooltip={disabledTooltip}
            />
          )}
        </div>
      </div>
    );
  }
);

ContactCard.propTypes = {
  contact: PropTypes.object.isRequired,
  onLinkedInClick: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
  // Opens the individual profile in a new browser tab (Cmd/Ctrl-click and the
  // right-click menu on the view-details icon). Omitted → plain click only.
  onOpenDetailsInNewTab: PropTypes.func,
  selected: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  personCrefoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Hoisted enrichment access (TD-2263). When provided, the card consumes it
  // instead of mounting its own useEnrichmentAccess() fetch. Optional so a
  // standalone card (Contact Detail page) still falls back to the hook.
  access: PropTypes.shape({
    enabled: PropTypes.bool,
    limitReached: PropTypes.bool
  }),
  // Stream B (TD-2085): 'discovered' switches the card to the discovered-contact
  // layout ('n/a' for CREFO-only fields, discover-reveal path)
  // and hides the bulk-select checkbox. Defaults to the stored-contact layout.
  // The FIELD layout additionally keys off the row itself (isDiscoveredRow):
  // persisted discovered rows arriving via the stored feed are recognised by
  // contact.source or the 'disc_' person_crefo_id prefix (TD-2273).
  source: PropTypes.oneOf(['platform', 'discovered']),
  // TD-2455: called after a successful reveal (a credit is deducted BE-side) so a
  // list parent can refresh the credit counter without a page reload. Pass a
  // stable reference. Omitted on the standalone Contact Detail card.
  onRevealSuccess: PropTypes.func,
  // TD-2427: in-flight bulk reveal overlay owned by KeyContactsTab. `keys` is the
  // set of submitted contact keys (contactKey), `fields` the submitted field(s),
  // `force` = skip OFF (re-fetch already-enriched fields → spinner over the
  // exposed value). Undefined when no bulk job is running → no per-card effect.
  bulkRevealing: PropTypes.shape({
    keys: PropTypes.instanceOf(Set),
    fields: PropTypes.arrayOf(PropTypes.oneOf(['email', 'phone'])),
    force: PropTypes.bool
  })
};

export default ContactCard;
